// Cookie consent — storage, change notification, and event plumbing for the
// PECR consent banner.
//
// The site sets only strictly necessary cookies today (Amazon Cognito auth)
// plus first-party browser storage for theme and form drafts. The "functional"
// and "analytics" categories below are consent-ready but currently gate
// nothing — when analytics or non-essential functional cookies are introduced,
// read `getConsent()` before loading them. The banner records the user's
// choice; it does not yet enforce that choice on existing storage.

/** localStorage key holding the consent record. Surfaced in the Cookie Policy. */
export const CONSENT_STORAGE_KEY = "thexis_cookie_consent";

// Bump when the set of categories changes. A stored record written under an
// older version is treated as "no decision yet" so the banner re-prompts.
export const CONSENT_VERSION = 1;

// Footer "Cookie settings" controls dispatch this on `window`; the banner
// listens for it and reopens its preferences panel.
export const OPEN_COOKIE_SETTINGS_EVENT = "thexis:open-cookie-settings";

export type CookieCategory = "necessary" | "functional" | "analytics";

export type CookieCategories = Record<CookieCategory, boolean>;

export interface CookieConsent {
  version: number;
  categories: CookieCategories;
  /** ISO 8601 timestamp of the decision. */
  updatedAt: string;
}

// `necessary` is always true — strictly necessary cookies do not need consent
// under PECR and cannot be switched off.
export const ALL_REJECTED: CookieCategories = {
  necessary: true,
  functional: false,
  analytics: false,
};

export const ALL_ACCEPTED: CookieCategories = {
  necessary: true,
  functional: true,
  analytics: true,
};

function isCategories(value: unknown): value is CookieCategories {
  if (!value || typeof value !== "object") return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.necessary === "boolean" &&
    typeof c.functional === "boolean" &&
    typeof c.analytics === "boolean"
  );
}

/**
 * Read the stored consent record. Returns `null` when there is no decision
 * yet, the record is unparseable, or it was written under an older version —
 * all of which mean the banner should prompt.
 */
export function getConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (
      parsed.version !== CONSENT_VERSION ||
      !isCategories(parsed.categories) ||
      typeof parsed.updatedAt !== "string"
    ) {
      return null;
    }
    // `necessary` is non-negotiable regardless of what was persisted.
    return {
      version: CONSENT_VERSION,
      categories: { ...parsed.categories, necessary: true },
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return null;
  }
}

// Change notification, so the banner can track consent via React's
// useSyncExternalStore rather than reading localStorage inside an effect.
const consentListeners = new Set<() => void>();

function notifyConsentChanged(): void {
  for (const listener of consentListeners) listener();
}

/** Subscribe to consent changes — the `subscribe` argument for useSyncExternalStore. */
export function subscribeConsent(listener: () => void): () => void {
  consentListeners.add(listener);
  // Also react to a choice made in another tab.
  function onStorage(event: StorageEvent) {
    if (event.key === CONSENT_STORAGE_KEY) notifyConsentChanged();
  }
  window.addEventListener("storage", onStorage);
  return () => {
    consentListeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** Persist a consent decision. `necessary` is forced on. */
export function setConsent(categories: CookieCategories): CookieConsent {
  const record: CookieConsent = {
    version: CONSENT_VERSION,
    categories: { ...categories, necessary: true },
    updatedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage can throw in private mode or when full — the banner still
    // closes for this session; the user is re-prompted on their next visit.
  }
  notifyConsentChanged();
  return record;
}

/** Reopen the consent preferences panel from anywhere (e.g. a footer link). */
export function openCookieSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}
